import { auth } from '../config/firebase';

/**
 * API Service for making authenticated requests to Firebase Functions
 * Automatically handles Firebase Auth tokens and token refresh
 */
export class ApiService {
  private baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://us-central1-adriftchatbotv3.cloudfunctions.net';

  /**
   * Get authentication headers with current user's ID token
   */
  async getAuthHeaders(): Promise<HeadersInit> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated. Please log in.');
    }

    try {
      const idToken = await user.getIdToken();
      
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      };
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw new Error('Failed to get authentication token');
    }
  }

  /**
   * Make an authenticated fetch request
   * Automatically handles token refresh if token is expired
   */
  async authenticatedFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      // If token expired, try to refresh and retry once
      if (response.status === 401) {
        console.log('Token expired, refreshing...');
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true); // Force refresh
          const newHeaders = await this.getAuthHeaders();
          
          // Retry the request with new token
          return fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers: {
              ...newHeaders,
              ...options.headers
            }
          });
        }
      }

      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // ===== Admin Endpoints =====

  /**
   * Update status message
   */
  async updateStatusMessage(message: string): Promise<Response> {
    return this.authenticatedFetch('/updateStatusMessage', {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<Response> {
    return this.authenticatedFetch(`/deleteConversation?conversationId=${conversationId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get all conversations
   */
  async getAllConversations(): Promise<Response> {
    return this.authenticatedFetch('/getAllConversations');
  }

  /**
   * Get conversation details
   */
  async getConversationDetails(conversationId: string): Promise<Response> {
    return this.authenticatedFetch(`/getConversationDetails?conversationId=${conversationId}`);
  }

  /**
   * Get messages for a conversation
   */
  async getMessages(conversationId: string): Promise<Response> {
    return this.authenticatedFetch(`/getMessages?conversationId=${conversationId}`);
  }

  /**
   * Create a rating
   */
  async createRating(data: { conversationId: string; messageId?: string; rating: string; notes?: string }): Promise<Response> {
    return this.authenticatedFetch('/createRating', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update a rating
   */
  async updateRating(ratingId: string, data: { rating?: string; notes?: string }): Promise<Response> {
    return this.authenticatedFetch('/updateRating', {
      method: 'PUT',
      body: JSON.stringify({ ratingId, ...data })
    });
  }

  /**
   * Delete a rating
   */
  async deleteRating(ratingId: string): Promise<Response> {
    return this.authenticatedFetch(`/deleteRating?ratingId=${ratingId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get all ratings
   */
  async getRatings(filters?: any): Promise<Response> {
    const queryParams = filters ? '?' + new URLSearchParams(filters).toString() : '';
    return this.authenticatedFetch(`/getRatings${queryParams}`);
  }

  /**
   * Get rating analytics
   */
  async getRatingAnalytics(): Promise<Response> {
    return this.authenticatedFetch('/getRatingAnalytics');
  }

  /**
   * Get analytics dashboard data
   */
  async getAnalyticsDashboard(): Promise<Response> {
    return this.authenticatedFetch('/getAnalyticsDashboard');
  }

  /**
   * Get AI chunks
   */
  async getAIChunks(filters?: any): Promise<Response> {
    const queryParams = filters ? '?' + new URLSearchParams(filters).toString() : '';
    return this.authenticatedFetch(`/getAIChunks${queryParams}`);
  }

  /**
   * Get a specific AI chunk
   */
  async getAIChunk(chunkId: string): Promise<Response> {
    return this.authenticatedFetch(`/getAIChunk?chunkId=${chunkId}`);
  }

  /**
   * Create an AI chunk
   */
  async createAIChunk(data: any): Promise<Response> {
    return this.authenticatedFetch('/createAIChunk', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update an AI chunk
   */
  async updateAIChunk(chunkId: string, data: any): Promise<Response> {
    return this.authenticatedFetch('/updateAIChunk', {
      method: 'PUT',
      body: JSON.stringify({ chunkId, ...data })
    });
  }

  /**
   * Delete an AI chunk
   */
  async deleteAIChunk(chunkId: string): Promise<Response> {
    return this.authenticatedFetch(`/deleteAIChunk?chunkId=${chunkId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Sync chunks to vector database
   */
  async syncChunks(): Promise<Response> {
    return this.authenticatedFetch('/syncChunks', {
      method: 'POST'
    });
  }

  /**
   * Get UI settings
   */
  async getUISettings(): Promise<Response> {
    return this.authenticatedFetch('/getUISettings');
  }

  /**
   * Save UI settings
   */
  async saveUISettings(settings: any): Promise<Response> {
    return this.authenticatedFetch('/saveUISettings', {
      method: 'POST',
      body: JSON.stringify(settings)
    });
  }

  /**
   * Update UI settings
   */
  async updateUISettings(settings: any): Promise<Response> {
    return this.authenticatedFetch('/updateUISettings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  /**
   * Update CTA buttons settings
   */
  async updateCTAButtonsSettings(buttons: any): Promise<Response> {
    return this.authenticatedFetch('/updateCTAButtonsSettings', {
      method: 'PUT',
      body: JSON.stringify({ buttons })
    });
  }

  // ===== Voice AI Endpoints =====

  /**
   * Sync saved greeting script to Vapi assistant firstMessage
   * Reads greetingScript from Firestore and PATCHes Vapi REST API
   */
  async syncVoiceGreeting(): Promise<Response> {
    return this.authenticatedFetch('/syncVoiceGreeting', {
      method: 'POST'
    });
  }
}

export const apiService = new ApiService();

